import { ShieldCheck } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import PortalHeader from '../components/PortalHeader';

const Section = ({ title, children }) => (
  <section className="space-y-4">
    <h2 className="text-base font-bold uppercase tracking-wide text-gray-900">
      {title}
    </h2>
    {children}
  </section>
);

const PrivacySection = ({ title, children }) => (
  <section className="space-y-4">
    <h2 className="text-base font-bold text-gray-900">
      {title}
    </h2>
    {children}
  </section>
);

const Subsection = ({ title, children }) => (
  <div className="space-y-3">
    <h3 className="text-sm font-bold text-gray-900">
      {title}
    </h3>
    {children}
  </div>
);

const Paragraph = ({ children }) => (
  <p className="text-sm leading-7 text-gray-700">
    {children}
  </p>
);

const BulletList = ({ items }) => (
  <ul className="ml-5 list-disc space-y-2 text-sm leading-7 text-gray-700">
    {items.map((item, index) => (
      <li key={index}>{item}</li>
    ))}
  </ul>
);

const LegalShell = ({ eyebrow, title, activePage, children }) => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <PortalHeader onBack={() => navigate(-1)} backLabel="Back">
          <nav className="flex items-center gap-2 text-sm font-medium">
            <Link
              to="/tos"
              className={`border-b-2 px-2 py-2 transition-colors sm:px-3 ${
                activePage === 'tos' ? 'border-cyan-700 text-cyan-800' : 'border-transparent text-slate-500 hover:text-slate-900'
              }`}
            >
              TOS
            </Link>
            <Link
              to="/privacy"
              className={`border-b-2 px-2 py-2 transition-colors sm:px-3 ${
                activePage === 'privacy' ? 'border-cyan-700 text-cyan-800' : 'border-transparent text-slate-500 hover:text-slate-900'
              }`}
            >
              Privacy
            </Link>
          </nav>
      </PortalHeader>

      <main className="mx-auto max-w-5xl px-0 py-5 sm:px-4 sm:py-8">
        <article className="border-y border-slate-200 bg-white sm:border-x">
          <div className="border-b border-gray-200 px-6 py-8 text-center sm:px-10">
            <p className="mb-3 text-xs font-bold uppercase tracking-wide text-cyan-800">
              {eyebrow}
            </p>
            <h1 className="text-2xl font-bold text-gray-950 sm:text-3xl">
              {title}
            </h1>
            <p className="mt-4 text-sm font-semibold text-gray-700">
              Effective Date: 29 September 2026
            </p>
          </div>

          <div className="space-y-8 px-6 py-8 sm:px-10">
            {children}
          </div>
        </article>

        <div className="flex items-center justify-center gap-2 border-b border-slate-100 bg-white px-4 py-3 text-center text-[10px] text-slate-400 sm:border-x">
          <ShieldCheck className="h-4 w-4" />
          <span>Secured by Koin Remit · Travel Rule Compliant</span>
        </div>
      </main>
    </div>
  );
};

export const TermsPage = () => (
  <LegalShell eyebrow="KOIN REMIT SENDING SIDE" title="TERMS & CONDITIONS" activePage="tos">
    <Section title="1. INTRODUCTION">
      <Paragraph>
        These Terms and Conditions (“Terms”) govern your use of the Koin Remit sending side application (the “Product”) and the associated non-custodial wallet (the “Wallet”) made available to you in connection with remittance services.
      </Paragraph>
      <Paragraph>
        By accessing, or using the Product, you (“you”, “your”, or the “User”) confirm that you have read, understood, and agreed to these Terms. If you do not agree, you must not use the Product.
      </Paragraph>
      <Paragraph>
        You represent and warrant that you are at least 18 years old (or the age of legal majority in your jurisdiction) and that your use of the Product is permitted under the laws applicable to you.
      </Paragraph>
      <Paragraph>
        Your use of the Product is also subject to Koin Remit Sending Side Privacy Policy, available at the end of these terms, which explains how personal data is processed, including data shared with third-party off-ramp partners.
      </Paragraph>
    </Section>

    <Section title="2. SERVICE DESCRIPTION">
      <Paragraph>
        2.1 Kai OS Technologies (Hong Kong) (“we”, “us”, “our”) provides a non-custodial blockchain-based wallet as part of the Product that enables users to:
      </Paragraph>
      <BulletList
        items={[
          <>receive digital assets (stablecoins) through a licensed 3<sup>rd</sup> party onramp service; and</>,
          'Transfer such on-ramped stablecoins to receiver non-custodial wallet;',
        ]}
      />
      <Paragraph>2.2 We do <strong>not</strong>:</Paragraph>
      <BulletList
        items={[
          'hold or control users’ digital assets or private keys;',
          'convert fiat to crypto or vice versa;',
          'act as a money transmitter, bank, or financial intermediary;',
          'guarantee the success or timing of any blockchain transaction.',
        ]}
      />
      <Paragraph>
        2.3 The Product serves only as a technical interface between the sender and recipient of digital assets. Nothing in the Product constitutes financial, investment, legal, or tax advice. You should seek independent advice before engaging in any transaction involving digital assets.
      </Paragraph>
      <Paragraph>
        2.4 The Product may not be available or may be restricted in certain jurisdictions. We do not represent that the Product or any feature thereof is appropriate or available for use in all locations. You are solely responsible for determining whether your use of the Product complies with applicable laws.
      </Paragraph>
    </Section>

    <Section title="3. REMITTANCE AND ON-RAMP FUNCTIONALITY">
      <Subsection title="3.1 Nature of Service">
        <Paragraph>
          The remittance feature of the Product enables users to onramp supported stablecoins from fiat via defined blockchain. The service does not involve foreign exchange. The on-ramping feature of the Product is managed by our licensed local partner.
        </Paragraph>
      </Subsection>
      <Subsection title="3.2 Delivery and Settlement">
        <Paragraph>
          Once a transaction is submitted to the blockchain, we do not control over processing speed, confirmation time, or network performance. Transactions are typically irreversible once confirmed on-chain.
        </Paragraph>
      </Subsection>
      <Subsection title="3.3 Responsibility for Sender Account">
        <Paragraph>You are solely responsible for verifying that:</Paragraph>
        <BulletList
          items={[
            'the information you input in the Product are true, correct and accurate;',
            'you are legally entitled to onramp and trigger the transfer to the receiver; and',
            'you comply with all applicable local regulations (including international remittance, tax and foreign exchange rules).',
          ]}
        />
      </Subsection>
      <Subsection title="3.4 Consumer Protection Notice.">
        <Paragraph>
          Certain consumer protection regimes applicable to traditional money transfer services may not apply to blockchain-based transactions facilitated through the Product.
        </Paragraph>
      </Subsection>
      <Subsection title="3.5 On-Ramp and Fiat-Crypto Conversion">
        <Paragraph>
          The Product enables you to on-ramp your fiat currency to digital assets (supported stablecoins) through third-party regulated partners authorised to provide such services in their respective jurisdictions.
        </Paragraph>
        <Paragraph>You acknowledge and agree that:</Paragraph>
        <BulletList
          items={[
            'All fiat conversion, fund transfers, and related KYC/AML processes are managed and performed by the licensed local partner.',
            'We don’t control or operate the on-ramp process and are not a party to any transaction between you and the on-ramp partner.',
            'We don’t guarantee the availability, timing, exchange rates, fees, or outcome of any on-ramp transaction, nor is it responsible for any losses, delays, account rejections, or compliance actions taken by the partner or its banking institutions.',
            'By using the on-ramp function, you acknowledge that you are engaging directly with the on-ramp, and that your personal data and transaction details may be shared as necessary to complete the transaction.',
            'We provide only the technical interface that allows you to connect your wallet to the on-ramp partner’s services, and accepts no liability arising from or in connection with those services',
          ]}
        />
      </Subsection>
      <Subsection title="KYC Verification and Information Sharing">
        <Paragraph>
          To access certain services, you may be required to complete identity verification through third-party compliance and identity verification providers selected by us from time to time. By initiating the identity verification process, you authorize us and such providers to collect, process, verify, and store identity information and supporting documentation submitted by you.
        </Paragraph>
        <Paragraph>
          You further authorize us to share identity verification results, customer due diligence information, sanctions screening results, anti-money laundering assessments, and other compliance-related information with our regulated partners, including on-ramp providers, aggregator partners, compliance service providers, transaction processors, liquidity providers, and other service providers involved in facilitating your transaction. Such parties may rely on this information to satisfy their own legal and regulatory obligations.
        </Paragraph>
      </Subsection>
    </Section>

    <Section title="4. USER RESPONSIBILITIES">
      <Paragraph>You are solely responsible for:</Paragraph>
      <BulletList
        items={[
          'securing your device and passwords;',
          'verifying all transaction details before confirming;',
          'complying with all applicable local laws and tax obligations;',
          'ensuring that your use of the Product does not violate sanctions or anti–money laundering rules.',
        ]}
      />
    </Section>

    <Section title="5. BLOCKCHAIN AND TRANSACTION RISKS">
      <Paragraph>By using the Product, you acknowledge that:</Paragraph>
      <BulletList
        items={[
          'blockchain transactions are public and irreversible;',
          'third-party blockchain networks are beyond our control;',
          'software bugs, network failures, or protocol changes could affect transactions or balances;',
          'your assets may lose value due to volatility, protocol failure, or regulatory actions.',
        ]}
      />
      <Paragraph>We shall not be liable for any damages or losses arising from:</Paragraph>
      <BulletList
        items={[
          'blockchain errors, forks, or downtime;',
          'smart contract vulnerabilities;',
          'inaccurate information or transaction details entered by you;',
          'external hacks or malware affecting your device.',
        ]}
      />
    </Section>

    <Section title="6. FEES AND CHARGES">
      <Paragraph>
        We may charge service fees, disclosed within the Product before a transaction is processed.
      </Paragraph>
      <Paragraph>
        Network gas fees are paid directly to the blockchain and are outside our control. All fees are non-refundable once the transaction is submitted.
      </Paragraph>
    </Section>

    <Section title="7. INTELLECTUAL PROPERTY">
      <Paragraph>
        All intellectual property rights in the Product, its interface, and related materials remain owned by us or our licensors. You are granted a limited, non-exclusive, non-transferable license to use the Product for lawful personal purposes only.
      </Paragraph>
    </Section>

    <Section title="8. DISCLAIMER AND LIMITATION OF LIABILITY">
      <Paragraph>
        The Product is provided “as is” and without warranties of any kind, whether express or implied. To the maximum extent permitted by law, we disclaim all liability for:
      </Paragraph>
      <BulletList
        items={[
          'any loss of funds, profits, data, or goodwill;',
          'network or software malfunctions;',
          'third-party actions, including hacking or fraud;',
          'indirect, incidental, or consequential damages.',
        ]}
      />
      <Paragraph>
        To the maximum extent permitted by applicable law, our total liability, if any, shall not exceed the fees you paid (if any) for use of the Product in the 12 months preceding the claim.
      </Paragraph>
    </Section>

    <Section title="9. TERMINATION">
      <Paragraph>
        You may stop using the Product at any time. We may suspend or terminate your access to the Product if you violate these Terms or use the Product unlawfully. We may further suspend, restrict, or terminate access if required to do so by applicable sanctions, AML, counter-terrorist financing, or other regulatory requirements.
      </Paragraph>
    </Section>

    <Section title="10. GOVERNING LAW AND DISPUTE RESOLUTION">
      <Paragraph>
        These Terms and any dispute or claim (including non-contractual disputes or claims) arising out of or in connection with them or their subject matter shall be governed by and construed in accordance with the laws of the Dubai International Financial Centre.
      </Paragraph>
      <Paragraph>
        Any dispute, controversy, or claim arising out of or in connection with these Terms, including any question regarding their existence, validity, or termination, shall be finally resolved by arbitration under the rules of the DIFC-LCIA Arbitration Centre, which rules are deemed to be incorporated by reference into this clause.
      </Paragraph>
      <Paragraph>
        The seat, or legal place, of arbitration shall be the Dubai International Financial Centre. The tribunal shall consist of one arbitrator. The language of the arbitration shall be English.
      </Paragraph>
      <Paragraph>
        If you are a consumer, nothing in this clause shall limit or affect any mandatory rights you may have under the laws of your country of residence, including your right to bring proceedings in your local courts where applicable.
      </Paragraph>
    </Section>

    <Section title="11. ENTIRE AGREEMENT">
      <Paragraph>
        These Terms, together with the privacy policy, constitute the entire agreement between you and us regarding your use of the Product.
      </Paragraph>
    </Section>
  </LegalShell>
);

export const PrivacyPage = () => (
  <LegalShell eyebrow="Koin Remit Sending Side" title="PRIVACY POLICY" activePage="privacy">
    <PrivacySection title="1. Introduction">
      <Paragraph>
        This Privacy Policy explains how KaiOS Technologies (Hong Kong) Limited (“we”, “us”, or “our”) collects, uses, shares, and protects personal information when you use the Koin Remit sending side application and related services (the “Product”).
      </Paragraph>
      <Paragraph>
        We are committed to protecting your privacy and handling your information responsibly and transparently. We collect and process personal data only to the extent necessary to operate the Product, comply with applicable laws, and enable our regulated partners to provide their services.
      </Paragraph>
      <Paragraph>
        By using the Product, you acknowledge and agree to the practices described in this Privacy Policy.
      </Paragraph>
    </PrivacySection>

    <PrivacySection title="2. Information We Collect">
      <Paragraph>
        Depending on how you use the Product, we may collect the following categories of information:
      </Paragraph>
      <Subsection title="2.1 Information You Provide Directly">
        <BulletList
          items={[
            'Basic identification information (such as name, date of birth, nationality)',
            'Contact information (such as phone number or email address, if applicable)',
            'Information required for compliance purposes (including identity verification details)',
          ]}
        />
      </Subsection>
      <Subsection title="2.2 Regulatory and Compliance Information">
        <Paragraph>
          To comply with applicable financial regulations (including AML, CFT, and “travel rule” requirements), we and/or our partners may collect and process:
        </Paragraph>
        <BulletList
          items={[
            'Government-issued identification details',
            'Transaction-related information (such as sender/recipient details)',
            'Wallet addresses and transaction identifiers',
            'Information required by regulated financial institutions or authorities',
          ]}
        />
        <Paragraph>
          Some of this information may be considered sensitive personal data under applicable law. It is collected solely to meet legal and regulatory obligations.
        </Paragraph>
      </Subsection>
      <Subsection title="2.3 Technical and Usage Information">
        <BulletList
          items={[
            'Device information (device type, operating system)',
            'Application usage data (feature usage, error logs)',
            'Network and security-related data necessary to operate and secure the Product',
          ]}
        />
        <Paragraph>
          We do not collect unnecessary data and do not use personal data for unrelated purposes.
        </Paragraph>
      </Subsection>
    </PrivacySection>

    <PrivacySection title="3. How We Use Your Information">
      <Paragraph>We use your information only for the following purposes:</Paragraph>
      <BulletList
        items={[
          'To operate, maintain, and improve the Product',
          'To enable transactions and technical interactions initiated by you',
          'To comply with applicable legal, regulatory, and compliance requirements',
          'To share required information with regulated partners so they can lawfully provide their services',
          'To detect, prevent, and address fraud, security, or technical issues',
        ]}
      />
      <Paragraph>
        We do not sell your personal data and do not use it for advertising or profiling unrelated to the Product.
      </Paragraph>
    </PrivacySection>

    <PrivacySection title="4. Sharing of Information">
      <Paragraph>
        We may share your information only as necessary and only with trusted parties, including:
      </Paragraph>
      <Subsection title="4.1 Regulated Partners and Receiver Side Operator">
        <Paragraph>
          We share relevant personal and transaction information with licensed third-party partners (such as off-ramp, on-ramp or compliance providers) and receiver side operator where required for them or their partners to:
        </Paragraph>
        <BulletList
          items={[
            'perform identity verification,',
            'process conversions, transfers, or offramp,',
            'comply with financial regulations applicable to their licensed activities.',
          ]}
        />
        <Paragraph>
          These partners process your information under their own privacy policies and regulatory obligations.
        </Paragraph>
        <Paragraph>
          This may include the sharing of completed KYC records, identity verification results, risk assessments, sanctions screening outcomes, and related compliance information through secure technical mechanisms, including API-based or tokenized identity-sharing solutions, where permitted by applicable law and necessary for the provision of services or regulatory compliance.
        </Paragraph>
      </Subsection>
      <Subsection title="4.2 Legal and Regulatory Authorities">
        <Paragraph>
          We may disclose information where required by law, regulation, court order, or competent authority.
        </Paragraph>
      </Subsection>
      <Subsection title="4.3 Service Providers">
        <Paragraph>
          We may use limited third-party service providers (e.g., infrastructure, security, analytics) who process data strictly on our behalf and under confidentiality obligations.
        </Paragraph>
      </Subsection>
    </PrivacySection>

    <PrivacySection title="5. International Data Transfers">
      <Paragraph>
        Your information may be processed or stored in jurisdictions outside your country of residence, including where our partners or service providers operate. Where required, we take reasonable steps to ensure that appropriate safeguards are in place to protect your information.
      </Paragraph>
    </PrivacySection>

    <PrivacySection title="6. Data Retention">
      <Paragraph>We retain personal data only for as long as necessary to:</Paragraph>
      <BulletList
        items={[
          'operate the Product,',
          'meet legal and regulatory requirements,',
          'resolve disputes, and',
          'enforce our agreements.',
        ]}
      />
      <Paragraph>
        Retention periods may be longer where required by financial regulations.
      </Paragraph>
    </PrivacySection>

    <PrivacySection title="7. Data Security">
      <Paragraph>
        We implement reasonable technical and organizational measures to protect personal data against unauthorized access, loss, misuse, or alteration. However, no system is completely secure, and we cannot guarantee absolute security.
      </Paragraph>
    </PrivacySection>

    <PrivacySection title="8. Your Rights">
      <Paragraph>Depending on your jurisdiction, you may have rights to:</Paragraph>
      <BulletList
        items={[
          'access your personal data,',
          'request correction of inaccurate data,',
          'request deletion of data (subject to legal and regulatory retention obligations),',
          'object to or restrict certain processing activities.',
        ]}
      />
      <Paragraph>
        Requests can be made by contacting us at the address below. Some requests may not be fulfilled where data must be retained for regulatory reasons.
      </Paragraph>
    </PrivacySection>

    <PrivacySection title="9. Third-Party Services">
      <Paragraph>
        The Product may link to or integrate with third-party services. We are not responsible for the privacy practices of those third parties. We encourage you to review their privacy policies before using their services.
      </Paragraph>
    </PrivacySection>

    <PrivacySection title="10. Changes to This Privacy Policy">
      <Paragraph>
        We may update this Privacy Policy from time to time. Any material changes will be push to the device for your acceptance or posted on our website, as applicable, and the “Effective Date” will be updated accordingly. Continued use of the Product after changes constitutes acceptance of the updated Privacy Policy.
      </Paragraph>
    </PrivacySection>

    <PrivacySection title="11. Contact Us">
      <Paragraph>
        If you have questions or requests regarding this Privacy Policy or our data practices, please contact:
      </Paragraph>
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm leading-7 text-gray-700">
        <p className="font-bold text-gray-900">KaiOS Technologies (Hong Kong) Limited</p>
        <p>Email: privacy@kaiostech.com</p>
      </div>
    </PrivacySection>
  </LegalShell>
);
